import { globalRef } from '@amazon/vinyl-util'

/** An origin that never resolves (RFC 6761), so a request the page did not route fails closed. */
const GATEWAY_ALIAS = 'https://audible-gateway.invalid'

/**
 * The library's own public endpoints, which only allow music.amazon.com's origin: its telemetry
 * events on a marketplace's unagi host and its remote configuration on its CDN. The shell sends
 * them for the page (`/upstream/<host><path>`), without a credential.
 */
const PUBLIC_ENDPOINT =
    /^https:\/\/(unagi\.amazon\.[a-z.]+(?=\/1\/events\/)|(?:db15rcvbvu5ga|d2rb61mge1m2o4|dv9hkwjdw9xgb)\.cloudfront\.net(?=\/configuration\/))/

type GatewayWindow = Window & typeof globalThis

const gatewayRef = globalRef(() => ({
    origin: '',
    routed: new WeakSet<GatewayWindow>(),
}))

/**
 * Sends the Audible library's requests to the shell's forwarder at `forwarderBase` and returns the
 * base URL to give the library. Its HTTP clients only send http(s) while the webview hands only
 * custom-scheme requests to the shell, so the library calls {@link GATEWAY_ALIAS}, and the page's
 * XHR and fetch send those requests, and those to its {@link PUBLIC_ENDPOINT}s, to the forwarder;
 * every other URL is untouched. Call before the library's module loads: its player keeps the fetch
 * it finds then.
 */
export function routeAudibleGateway(forwarderBase: string, win: GatewayWindow = window): string {
    const { pathname } = new URL(forwarderBase)
    const gateway = gatewayRef.value
    gateway.origin = forwarderBase.slice(0, forwarderBase.length - pathname.length)
    if (!gateway.routed.has(win)) {
        gateway.routed.add(win)
        installRoute(win)
    }
    return GATEWAY_ALIAS + pathname
}

function route(url: string): string {
    const { origin } = gatewayRef.value
    if (url.startsWith(`${GATEWAY_ALIAS}/`)) return origin + url.slice(GATEWAY_ALIAS.length)
    const host = PUBLIC_ENDPOINT.exec(url)?.[1]
    return host ? `${origin}/upstream/${host}${url.slice(`https://${host}`.length)}` : url
}

function installRoute(win: GatewayWindow): void {
    const xhr = win.XMLHttpRequest.prototype
    // Captured before the patch below replaces it, so the patch can call the original.
    const open = Object.getOwnPropertyDescriptor(xhr, 'open')?.value as (
        this: XMLHttpRequest,
        ...args: unknown[]
    ) => void
    xhr.open = function (
        this: XMLHttpRequest,
        method: string,
        url: string | URL,
        ...rest: unknown[]
    ) {
        const asked = String(url)
        const sent = route(asked)
        if (sent !== asked)
            Object.defineProperty(this, 'responseURL', { value: asked, configurable: true })
        open.call(this, method, sent, ...rest)
    }
    const fetch = win.fetch.bind(win)
    win.fetch = async (input, init) => {
        const asked = input instanceof win.Request ? input.url : String(input)
        const sent = route(asked)
        if (sent === asked) return fetch(input, init)
        const request = input instanceof win.Request ? new win.Request(sent, input) : sent
        // The library resolves a manifest's relative segments against the URL it asked for.
        const response = await fetch(request, init)
        return Object.defineProperty(response, 'url', { value: asked, configurable: true })
    }
}
